import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { payloadString } from '../realtime-sync.payload';
import type { DataChangedEvent } from '../realtime-sync.types';

export async function handlePurchaseCompleted(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;

  wsLogger.info('[RealtimeSync] Purchase completed:', {
    purchaseId: event.id,
    tokenAmount: payload?.token_amount,
  });

  // API-driven messages: server ships localized title/message; balance refresh
  // happens regardless so the new value is reflected even when toast is skipped.
  const purchaseCompleteTitle = payloadString(payload, 'title');
  if (purchaseCompleteTitle) {
    const description = payloadString(payload, 'message');
    toast.success(purchaseCompleteTitle, {
      ...(description && { description }),
      duration: 6000,
    });
  }

  // Invalidate balance + profile so subscribers refetch with the new tokens.
  // Balance is the load-bearing one (purchase just added tokens); profile/user
  // get swept for forward-compatibility with future profile queries.
  queryClient.invalidateQueries({ queryKey: cacheKeys.balance });
  queryClient.invalidateQueries({ queryKey: cacheKeys.profile });
  queryClient.invalidateQueries({ queryKey: cacheKeys.user });
  // Cross-device/tab backstop: a purchase on another device flips ownership/
  // price on the storefront + token-package catalogs. Drop both prefixes so a
  // tab that didn't initiate the buy stops showing the stale buy CTA/price.
  queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
  queryClient.invalidateQueries({ queryKey: cacheKeys.tokenPackages.all });
}

export async function handlePurchaseFailed(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;

  wsLogger.warn('[RealtimeSync] Purchase failed:', {
    purchaseId: event.id,
    reason: payload?.reason,
  });

  // API-driven messages: skip toast if payload lacks localized title.
  const purchaseFailedTitle = payloadString(payload, 'title');
  if (purchaseFailedTitle) {
    const description =
      payloadString(payload, 'message') ?? payloadString(payload, 'reason');
    toast.error(purchaseFailedTitle, {
      ...(description && { description }),
      duration: 8000,
    });
  }
}
